import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Box,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

const BRAND_NAVY = '#002F87';
const BRAND_TEAL = '#015682';

const sectionPaperSx = {
  height: '100%',
  borderRadius: '10px',
  overflow: 'hidden',
  border: '1px solid #e8eaed',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  '&:before': { display: 'none' },
};

const summarySx = {
  bgcolor: '#f7f9fc',
  minHeight: 48,
  px: 2,
  '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: BRAND_NAVY,
  },
};

const detailsSx = {
  px: 2,
  pb: 2,
  pt: 0,
  bgcolor: '#fff',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#fafbff',
    borderRadius: '8px',
  },
  '& input': {
    fontSize: '0.85rem',
    padding: '8px 12px',
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.78rem',
  },
};

function displayValue(val) {
  if (val == null || String(val).trim() === '') return '—';
  return String(val);
}

function ReadFields({ pairs }) {
  return (
    <>
      {pairs.map(({ label, value }) => (
        <Box key={label} sx={{ mb: 1.25 }}>
          <TextField
            fullWidth
            size="small"
            margin="dense"
            variant="outlined"
            label={label}
            value={displayValue(value)}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true, sx: { color: 'text.secondary' } }}
            sx={fieldSx}
          />
        </Box>
      ))}
    </>
  );
}

function SectionAccordion({ title, icon: IconComponent, headerAside, children, defaultExpanded }) {
  return (
    <Accordion disableGutters defaultExpanded={defaultExpanded} sx={sectionPaperSx}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
        {IconComponent && (
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: BRAND_TEAL,
              '& svg': { fontSize: 20 },
            }}
          >
            <IconComponent />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: BRAND_NAVY, lineHeight: 1.25 }}>
            {title}
          </Typography>
        </Box>
        {headerAside ? (
          <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
            {headerAside}
          </Box>
        ) : null}
      </AccordionSummary>
      <AccordionDetails sx={detailsSx}>{children}</AccordionDetails>
    </Accordion>
  );
}

/** 2×2 layout: Customer | Business on top row; Billing | Shipping bottom — full width of panel */
const gridSx = {
  display: 'grid',
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
  gap: 2.25,
  alignItems: 'stretch',
};

export default function OrderCustomerSnapshot({ order }) {
  if (!order) {
    return (
      <Typography variant="body2" color="text.secondary">
        Load an order to see customer details for this placement.
      </Typography>
    );
  }

  const accountType = (order.order_customer_account_type || '').trim();
  const hasBizSnapshotField = [
    order.snapshot_business_legal_name,
    order.snapshot_business_registration_number,
    order.snapshot_business_vat_tax_number,
    order.snapshot_business_eori_import_export_number,
  ].some((v) => v != null && String(v).trim() !== '');

  // Sub-user business checkouts set placed_by_name; API may also hydrate account type for legacy rows.
  const isBusinessProfile =
    accountType === 'Business' ||
    (accountType === '' && (hasBizSnapshotField || !!order.placed_by_name));

  const customerPairs = [
    { label: 'Name (on order)', value: order.customer_name },
    { label: 'Email', value: order.customer_email },
    { label: 'Phone', value: order.customer_phone },
    ...(order.placed_by_name ? [{ label: 'Placed by', value: order.placed_by_name }] : []),
  ];

  const billingPairs = [
    { label: 'Billing name', value: order.billing_name },
    { label: 'Street address', value: order.billing_address_1 },
    { label: 'Suburb / locality', value: order.billing_address_2 },
    { label: 'City / town', value: order.billing_city },
    { label: 'State / province / region', value: order.billing_state },
    { label: 'Postal / ZIP code', value: order.billing_postal_code },
    { label: 'Country', value: order.billing_country },
  ];

  const shippingPairs = [
    { label: 'Shipping name', value: order.shipping_name },
    { label: 'Shipping phone', value: order.shipping_phone },
    { label: 'Street address', value: order.shipping_address_1 },
    { label: 'Suburb / locality', value: order.shipping_address_2 },
    { label: 'City / town', value: order.shipping_city },
    { label: 'State / province / region', value: order.shipping_state },
    { label: 'Postal / ZIP code', value: order.shipping_postal_code },
    { label: 'Country', value: order.shipping_country },
  ];

  const businessPairs = [
    { label: 'Company name', value: order.snapshot_business_legal_name },
    { label: 'Registration number', value: order.snapshot_business_registration_number },
    { label: 'VAT number', value: order.snapshot_business_vat_tax_number },
    { label: 'PDPL / import-export', value: order.snapshot_business_eori_import_export_number },
  ];

  const businessHeaderChip = isBusinessProfile ? (
    <Chip
      icon={<BusinessIcon sx={{ fontSize: '18px !important' }} />}
      label="Business"
      size="small"
      sx={{
        fontWeight: 600,
        bgcolor: BRAND_NAVY,
        color: '#fff',
        '& .MuiChip-icon': { color: '#fff' },
      }}
    />
  ) : (
    <Chip
      label="Individual"
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 600,
        color: BRAND_TEAL,
        borderColor: 'rgba(1, 86, 130, 0.45)',
      }}
    />
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Box sx={gridSx}>
        <Box sx={{ minWidth: 0 }}>
          <SectionAccordion icon={PersonOutlineIcon} title="Customer information" defaultExpanded>
            <ReadFields pairs={customerPairs} />
          </SectionAccordion>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <SectionAccordion icon={BusinessIcon} title="Business information" defaultExpanded headerAside={businessHeaderChip}>
            <ReadFields pairs={businessPairs} />
          </SectionAccordion>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <SectionAccordion title="Billing address" defaultExpanded>
            <ReadFields pairs={billingPairs} />
          </SectionAccordion>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <SectionAccordion title="Shipping address" defaultExpanded>
            <ReadFields pairs={shippingPairs} />
          </SectionAccordion>
        </Box>
      </Box>
    </Box>
  );
}
